import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Sparkles, Send, X, Loader2 } from "lucide-react";
import { askEva, type EvaTurn } from "./evaClient";
import type { buildEvaContext as BuildEvaContext } from "./knowledgeBase";
import styles from "./EvaWidget.module.scss";

interface ChatMessage {
  id: number;
  role: "user" | "assistant";
  content: string;
  followups?: string[];
  error?: boolean;
}

const STARTERS = [
  "What's the status of our biggest active account?",
  "Which orders are at risk right now?",
  "Any low-stock raw materials I should reorder?",
  "How's production — any overdue batches?",
];

let msgId = 0;

/**
 * E.V.A — Electromotive Virtual Analyst. A floating assistant available across
 * i-SYNERGY. Staff type a question; E.V.A answers grounded in the B2B Pipeline,
 * Historical Data, Customer Feedback, CRM Geomapping, Production, Inventory and
 * Sales. (Voice input / spoken replies were removed — E.V.A is text-only now,
 * so she no longer uses the Azure Speech service.)
 */
export function EvaWidget() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [busy, setBusy] = useState(false);

  const location = useLocation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Cache the dynamically-imported knowledge base (heavy baked data) after first use.
  const buildContextRef = useRef<typeof BuildEvaContext | null>(null);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy]);

  const send = useCallback(
    async (question: string) => {
      const q = question.trim();
      if (!q || busy) return;

      const history: EvaTurn[] = messages
        .slice(-6)
        .map((m) => ({ role: m.role, content: m.content }));

      setMessages((prev) => [...prev, { id: ++msgId, role: "user", content: q }]);
      setInput("");
      setBusy(true);

      try {
        if (!buildContextRef.current) {
          buildContextRef.current = (await import("./knowledgeBase")).buildEvaContext;
        }
        const { contextText, asOf } = await buildContextRef.current(q, location.pathname);
        const res = await askEva(q, contextText, history, asOf);
        setMessages((prev) => [
          ...prev,
          { id: ++msgId, role: "assistant", content: res.answer, followups: res.followups },
        ]);
      } catch (err) {
        setMessages((prev) => [
          ...prev,
          {
            id: ++msgId,
            role: "assistant",
            error: true,
            content:
              err instanceof Error
                ? `I hit a snag: ${err.message}`
                : "Something went wrong reaching my brain. Please try again.",
          },
        ]);
      } finally {
        setBusy(false);
      }
    },
    [busy, messages, location.pathname]
  );

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void send(input);
  };

  if (!open) {
    return (
      <button
        className={styles.launcher}
        onClick={() => setOpen(true)}
        aria-label="Ask E.V.A"
        title="Ask E.V.A"
      >
        <Sparkles size={20} />
        <span className={styles.launcherLabel}>Ask E.V.A</span>
      </button>
    );
  }

  return (
    <div className={styles.panel} role="dialog" aria-label="E.V.A assistant">
      <header className={styles.header}>
        <div className={styles.brand}>
          <div className={styles.avatar}>
            <Sparkles size={16} />
          </div>
          <div>
            <div className={styles.name}>E.V.A</div>
          </div>
        </div>
        <div className={styles.headerActions}>
          <button
            className={styles.iconBtn}
            onClick={() => setOpen(false)}
            title="Close"
            aria-label="Close E.V.A"
          >
            <X size={16} />
          </button>
        </div>
      </header>

      <div className={styles.messages} ref={scrollRef}>
        {messages.length === 0 && (
          <div className={styles.welcome}>
            <p className={styles.welcomeLead}>
              Hi, I'm <b>E.V.A</b>. Ask me about a customer, an order, a production batch,
              raw-material stock, or sales — I pull from across i-SYNERGY: CRM,
              Manufacturing, Inventory, and Accounting.
            </p>
            <div className={styles.starters}>
              {STARTERS.map((s) => (
                <button key={s} className={styles.starter} onClick={() => void send(s)}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m) => (
          <div
            key={m.id}
            className={`${styles.msg} ${m.role === "user" ? styles.msgUser : styles.msgEva} ${
              m.error ? styles.msgError : ""
            }`}
          >
            {m.role === "assistant" && !m.error && (
              <div className={styles.msgAvatar}>
                <Sparkles size={13} />
              </div>
            )}
            <div className={styles.msgBody}>
              <div className={styles.msgText}>{m.content}</div>
              {m.followups && m.followups.length > 0 && (
                <div className={styles.followups}>
                  {m.followups.map((f) => (
                    <button key={f} className={styles.followup} onClick={() => void send(f)}>
                      {f}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}

        {busy && (
          <div className={`${styles.msg} ${styles.msgEva}`}>
            <div className={styles.msgAvatar}>
              <Sparkles size={13} />
            </div>
            <div className={styles.msgBody}>
              <div className={styles.thinking}>
                <Loader2 size={14} className={styles.spin} /> Analyzing the data…
              </div>
            </div>
          </div>
        )}
      </div>

      <form className={styles.inputRow} onSubmit={onSubmit}>
        <input
          ref={inputRef}
          className={styles.input}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask E.V.A anything in i-SYNERGY…"
          disabled={busy}
        />
        <button
          type="submit"
          className={styles.sendBtn}
          disabled={busy || !input.trim()}
          title="Send"
          aria-label="Send"
        >
          <Send size={16} />
        </button>
      </form>
      <div className={styles.footer}>
        Grounded across i-SYNERGY · CRM · Production · Sales · Inventory
      </div>
    </div>
  );
}
